import Phaser from 'phaser';
import { t } from '@i18n/index';
import { PALETTE, TEXT_COLOUR, readoutStyle, FONT, px } from '../theme';
import { createListRow, createPanel } from '../ui/panel';
import { createButton } from '../ui/button';
import type { VoyageChoice, VoyageEvent } from '@core/voyage/events';
import type { WorldMapScene } from './WorldMapScene';
import type { Campaign } from '@core/campaign/campaign';
import { maybeShowTutorial } from '../ui/adviserPanel';

/**
 * Ein Ereignis unterwegs.
 *
 * Laeuft als eigene Szene ueber der pausierten Karte. Solange hier etwas
 * ansteht, faehrt das Schiff nicht weiter — die Entscheidung soll ueberlegt
 * getroffen werden, nicht unter Reaktionsdruck.
 */
export class VoyageEventScene extends Phaser.Scene {
  private event!: VoyageEvent;
  private chosen: VoyageChoice | null = null;

  constructor() {
    super('VoyageEvent');
  }

  init(data: { event: VoyageEvent }): void {
    this.event = data.event;
    this.chosen = null;
  }

  create(): void {
    this.redraw();

    // Der richtige Moment fuer den Hinweis auf die Kosten unterwegs: wenn
    // gerade eine Entscheidung ansteht, die welche verursacht.
    const campaign = this.registry.get('campaign') as Campaign | undefined;
    if (campaign !== undefined) maybeShowTutorial(this, campaign, 'voyage');
  }

  private redraw(): void {
    this.children.removeAll();

    const { width, height } = this.scale;
    const centreX = width / 2;
    const panelWidth = Math.min(620, width - 80);
    const panelHeight = 300;
    const left = centreX - panelWidth / 2;
    const top = height / 2 - panelHeight / 2;

    // Die Karte darunter abdunkeln, damit der Blick hier landet.
    this.add.rectangle(0, 0, width, height, PALETTE.abyss, 0.82).setOrigin(0, 0);
    createPanel(this, left, top, panelWidth, panelHeight);

    this.add.text(left + 24, top + 22, t(`voyage.kind.${this.event.kind}`), {
      fontFamily: FONT.mono,
      fontSize: px(11),
      color: TEXT_COLOUR.coast,
    });

    this.add.text(left + 24, top + 46, t(this.event.textKey), {
      ...readoutStyle(13),
      wordWrap: { width: panelWidth - 48 },
      lineSpacing: 4,
    });

    if (this.chosen === null) {
      this.renderChoices(left, top + 140, panelWidth);
    } else {
      this.renderOutcome(left, top + 140, panelWidth, top + panelHeight);
    }
  }

  private renderChoices(left: number, y: number, panelWidth: number): void {
    this.event.choices.forEach((choice, index) => {
      createListRow(this, left + 24, y + index * 50, {
        width: panelWidth - 48,
        height: 44,
        title: t(choice.labelKey),
        detail: describeCost(choice),
        onClick: () => {
          this.chosen = choice;
          this.redraw();
        },
      });
    });
  }

  private renderOutcome(left: number, y: number, panelWidth: number, panelBottom: number): void {
    const choice = this.chosen as VoyageChoice;

    this.add.text(left + 24, y, t(choice.outcomeKey), {
      ...readoutStyle(12),
      color: PALETTE.inkDim,
      wordWrap: { width: panelWidth - 48 },
      lineSpacing: 3,
    });

    // Was die Wahl konkret bewirkt hat — untereinander, damit es sich nicht
    // gegenseitig verdeckt.
    let noteY = y + 74;
    const note = (text: string, colour: string): void => {
      this.add.text(left + 24, noteY, text, {
        ...readoutStyle(11),
        color: colour,
        fontStyle: 'italic',
        wordWrap: { width: panelWidth - 48 },
      });
      noteY += 18;
    };

    if (choice.soulsRescued > 0) {
      note(t('voyage.souls.rescued', { count: choice.soulsRescued }), TEXT_COLOUR.base);
    }
    if (choice.yieldsEvidence) {
      note(t('voyage.evidence.gained'), TEXT_COLOUR.landmark);
    }
    if (choice.hullDamage > 0.5) {
      note(t('voyage.hull.damaged', { value: Math.round(choice.hullDamage) }), TEXT_COLOUR.danger);
    }

    createButton(
      this,
      left + panelWidth / 2,
      panelBottom - 34,
      t('voyage.continue'),
      () => {
        const worldMap = this.scene.get('WorldMap') as unknown as WorldMapScene;
        this.scene.stop();
        worldMap.resolveEvent(choice);
      },
      { width: 200, height: 34, fontSize: 12 },
    );
  }
}

/** Kosten einer Wahl in Worten — die Grundlage der Abwägung. */
function describeCost(choice: VoyageChoice): string {
  const parts: string[] = [];
  if (choice.delayHours > 0) {
    parts.push(t('voyage.cost.hours', { hours: choice.delayHours.toFixed(1) }));
  }
  if (choice.fuelCost > 0) {
    parts.push(t('voyage.cost.fuel', { fuel: Math.round(choice.fuelCost) }));
  }
  // Ein negativer Betrag ist Nachschub. Er gehoert genauso deutlich hin wie
  // die Kosten — beim Bunkerhalt ist er der ganze Punkt.
  if (choice.fuelCost < 0) {
    parts.push(t('voyage.gain.fuel', { fuel: Math.round(-choice.fuelCost) }));
  }
  if (choice.hullDamage > 0.5) {
    parts.push(t('voyage.cost.hull', { value: Math.round(choice.hullDamage) }));
  }
  // Was eine Wahl einbringt, gehoert neben das, was sie kostet — sonst ist es
  // keine Abwaegung, sondern eine Warnung.
  if (choice.soulsRescued > 0) {
    parts.push(t('voyage.gain.souls', { count: choice.soulsRescued }));
  }
  return parts.length === 0 ? t('voyage.cost.none') : parts.join('  ·  ');
}
